import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { KpiSection } from '../../../data-models/company-financials.data-model';
import { COMPANY_VIEW_TYPE } from '../../../data-models/view-config.data-model';
import { customFieldsListAtom } from '../../../services/state/AdminPanel/CustomFieldsStateJ';
import { getKpiFieldKey, kpiConfigByKeyMapAtom } from '../../../services/state/KPIConfigState';
import { columnsByPathState } from '../state/ViewStateJ';
import { GroupedCheckboxPicker, IPickerOption } from './GroupedCheckboxPicker';
import { useIsColumnAllowed } from './multiPeriodReportingPermUtils';
import { getMprItemKey, MprItem } from './multiPeriodReportingUtils';

export const MprItemGroup = {
  actualKpis: 'KPIs · Actual',
  budgetKpis: 'KPIs · Budget',
  metrics: 'Metrics',
  customFields: 'Custom fields',
} as const;

/** Every item the report can hold, in picker order: KPIs by section, then metrics, then custom fields. */
export function useMprItemOptions(): IPickerOption<MprItem>[] {
  const kpisByKey = useAtomValue(kpiConfigByKeyMapAtom);
  const columnsByPath = useAtomValue(columnsByPathState(COMPANY_VIEW_TYPE.RETURN_FORECAST));
  const customFields = useAtomValue(customFieldsListAtom);
  const isColumnAllowed = useIsColumnAllowed();

  return useMemo(() => {
    const byLabel = (a: IPickerOption<MprItem>, b: IPickerOption<MprItem>) => a.label.localeCompare(b.label);
    const toOption = (item: MprItem, label: string, group: string): IPickerOption<MprItem> => ({
      key: getMprItemKey(item),
      value: item,
      label,
      group,
    });
    const kpis = (section: KpiSection, group: string) =>
      [...kpisByKey.values()]
        .flatMap((kpi) => {
          const kpiKey = getKpiFieldKey(kpi);
          return kpiKey ? [toOption({ kpiKey, section }, kpi.displayName, group)] : [];
        })
        .sort(byLabel);
    const metrics = [...columnsByPath.entries()]
      .filter(([path]) => isColumnAllowed(path))
      .map(([path, meta]) => toOption({ columnPath: path }, meta.displayName, MprItemGroup.metrics))
      .sort(byLabel);
    const fields = (customFields ?? [])
      .map((field) => toOption({ customFieldId: field.id }, field.displayName, MprItemGroup.customFields))
      .sort(byLabel);
    return [
      ...kpis(KpiSection.actual, MprItemGroup.actualKpis),
      ...kpis(KpiSection.budget, MprItemGroup.budgetKpis),
      ...metrics,
      ...fields,
    ];
  }, [columnsByPath, customFields, isColumnAllowed, kpisByKey]);
}

interface IMprItemPickerProps {
  label: string;
  /** The report's items, in order; the picker's checked options. */
  selected: MprItem[];
  onChange: (items: MprItem[]) => void;
}

/** A checkbox multi-select over KPIs, metrics and custom fields; its checked state is the item list. */
export function MprItemPicker({ label, selected, onChange }: IMprItemPickerProps) {
  const options = useMprItemOptions();
  return (
    <GroupedCheckboxPicker
      label={label}
      options={options}
      selected={selected}
      keyOf={getMprItemKey}
      onChange={onChange}
    />
  );
}
